import { useState, useEffect } from "react";
import { dirOf } from "../utils/textDir";
import { T, UI } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { useIsMobile } from "../utils/hooks";
import TradingViewChart from "./TradingViewChart";
import FinancialsPanel from "./FinancialsPanel";
import CompanyProfile from "./CompanyProfile";
import HoldingMarkCard from "./HoldingMarkCard";
import { HoldingToday } from "./TodayMoves";
import { fetchNextReport, HOUR_HE } from "../utils/nextReport";
import { dateNumeric } from "../utils/formatDate";
import LTR from "./LTR";
import CardKind from "./CardKind";
import { useStockMetrics, useCompanyProfile, fetchQuote } from "../utils/finnhub";
import { fetchCompanyNews } from "../utils/finnhubNews";
import { useHebrewNews, waitingNote } from "../utils/headline";
import { Skeleton } from "./Skeleton";
import { isAdvisoryHeadline } from "../constants/advisory";
import { exchangeHe, countryHe, industryHe } from "../constants/profileLabels";
import { isAbout } from "../utils/newsMatch";
import { isAdvisoryArticle } from "../constants/advisory";
import { isLogoUrl, looksLikeLogo } from "../utils/newsImage";
import Icon from "./Icon";
import { FINNHUB_VALUATION_ENABLED } from "../constants/features";
import Headline from "./Headline";
import { Amount, CompactMoney } from "./Money";
import EmptyState from "./EmptyState";
import FilingSummary from "./FilingSummary";
import { fetchSecFilings } from "../utils/secFilings";

// ─── Key Stats Panel ───────────────────────────────────────────────────────────
// Finnhub, and only Finnhub. The KEY_STATS table that used to back the empty
// cells held 2024 figures for nine companies under a "דמו" tag; a paid product
// does not ship a placeholder for a number it can fetch, and for a number it
// cannot fetch it says so. Annual revenue is the one row that went with the
// table: Finnhub's basic metrics carry per-share and growth figures, not the
// total, and summing the SEC segment rows is a number no company reported
// (DECISIONS.md, 2026-09-09). The profile tab carries the breakdown itself.
export function KeyStatsPanel({ symbol }) {
  const live    = useStockMetrics(symbol);
  const profile = useCompanyProfile(symbol);
  // Today's range comes from the quote, which the metrics endpoint does not
  // carry. Same cache as the portfolio's price refresh.
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchQuote(symbol).then(q => { if (alive && q?.h > 0) setQuote(q); });
    return () => { alive = false; };
  }, [symbol]);

  // Finnhub returns marketCapitalization in millions
  // Finnhub returns marketCapitalization in millions; through CompactMoney.
  const fmtMcap = v => (v ? <CompactMoney value={v * 1e6} size={null} weight={null}/> : null);

  // Provenance per cell: `live` marks a Finnhub value; a cell without one
  // renders "—". The marker sits on the neutral ramp, deliberately — where a
  // number came from is metadata, not a judgement, so neither gold nor
  // green/red applies to it.
  const liveMcap = !!(live.isLive && profile?.marketCapitalization);
  const items = [
    // A range is one LTR run on one line: in an RTL cell the two ends used to
    // swap places and wrap apart ("$271.10 –" over "$268.40").
    { label: "טווח יומי",         val: quote?.l > 0 && quote?.h > 0 ? <PriceRange lo={quote.l} hi={quote.h}/> : "—", live: !!quote, range: !!quote },
    { label: "טווח שנתי",         val: live.low52 > 0 && live.high52 > 0 ? <PriceRange lo={live.low52} hi={live.high52}/> : "—", live: !!live.high52, range: live.low52 > 0 && live.high52 > 0 },
    // Everything below the two ranges is a Finnhub figure beyond the price
    // and follows FINNHUB_VALUATION_ENABLED (constants/features.js).
    ...(FINNHUB_VALUATION_ENABLED ? [{ label: "שווי שוק",          val: liveMcap ? fmtMcap(profile.marketCapitalization) : "—", live: liveMcap }] : []),
    // P/E and EPS are hidden while FINNHUB_VALUATION_ENABLED is false, the
    // same as on the company page (constants/features.js, DECISIONS.md).
    ...(FINNHUB_VALUATION_ENABLED ? [{ label: "רווח בסיסי למניה", val: live.eps ? <Amount value={live.eps} currency="USD" decimals={2} size={null} weight={null}/> : "—", live: !!live.eps }] : []),
    ...(FINNHUB_VALUATION_ENABLED ? [{ label: "Beta",              val: live.beta ? live.beta.toFixed(2) : "—", live: !!live.beta }] : []),
    ...(FINNHUB_VALUATION_ENABLED ? [{ label: "P/E",              val: live.pe ? live.pe.toFixed(1) : "—", live: !!live.pe }] : []),
    ...(FINNHUB_VALUATION_ENABLED ? [{ label: "תשואת דיבידנד",    val: live.dividendYield != null ? (live.dividendYield > 0 ? `${live.dividendYield.toFixed(2)}%` : "אין") : "—", live: live.dividendYield != null }] : []),
  ];
  const anyLive = items.some(i => i.live);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {anyLive ? (
        <div style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <span className="live-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: UI.text.secondary, display: "inline-block" }}/>
          נתוני Finnhub · תא ריק = אין נתון לנייר זה
        </div>
      ) : (
        <EmptyState
          icon="chart"
          line="אין נתוני מפתח זמינים"
          sub={"הנתונים נלקחים מ-Finnhub. לקרנות סל ולניירות מחוץ לארה\"ב אין שם מדדים בסיסיים, או שהם טרם נטענו."}
        />
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {items.map(item => (
          <div key={item.label} style={{ background: UI.surface.page, borderRadius: 6, padding: "14px 14px", border: `1px solid ${item.live ? UI.line.strong : UI.line.hairline}` }}>
            <div style={{ ...TYPE.label, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
              {item.label}
              {item.live && <span style={{ fontSize: 9, color: UI.text.secondary, fontWeight: 700 }}>●</span>}
            </div>
            {item.range
              ? <div style={{ ...TYPE.value, fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", textAlign: "right" }}><bdi dir="ltr">{item.val}</bdi></div>
              : <div style={{ ...TYPE.value, fontWeight: 700 }}>{item.val}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Company Profile Panel ─────────────────────────────────────────────────────
// Finnhub's profile2, and only that. The COMPANY_PROFILES table that used to
// back this panel held a founding year and a head-count for five companies,
// frozen in 2024 and shown without a label; Finnhub carries neither field, so
// those two rows are gone rather than filled from a table that could only ever
// go staler. When the profile has not arrived (or Finnhub has none — it
// answers `{}` for an ETF) the panel says so instead of drawing a grid of
// placeholders.
export function CompanyProfilePanel({ symbol }) {
  const live    = useCompanyProfile(symbol);

  if (!live) {
    return (
      <EmptyState
        icon="building"
        line="אין פרופיל חברה זמין"
        sub="הפרופיל נלקח מ-Finnhub. לקרנות סל ולחלק מהניירות אין פרופיל חברה שם, ואין ממה להציג."
      />
    );
  }

  const website = live.weburl;
  const ipo     = live.ipo;
  const country = live.country;
  const exchange= live.exchange;
  const industry= live.finnhubIndustry;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        <span className="live-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: UI.text.secondary, display: "inline-block" }}/>
        פרופיל מ-Finnhub
      </div>

      {/* The hand-written Hebrew description that used to sit here is gone.
          It had no source, and the "מה החברה עושה" tab below now carries the
          company's own words from its annual report instead. What remains in
          this panel is the live Finnhub facts grid. */}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[
          // Domain shown as plain text — the app never navigates out.
          { label: "אתר החברה",     val: (website && website !== "#") ? <span dir="ltr" style={{ color: UI.text.secondary, fontWeight: 700, fontSize: 12, unicodeBidi: "isolate" }}>{website.replace(/https?:\/\//,"").replace(/\/$/,"")}</span> : "—" },
          { label: "תאריך הנפקה",   val: ipo && dateNumeric(ipo) ? <LTR className="si-num">{dateNumeric(ipo)}</LTR> : "—" },  // DD/MM/YYYY, not Finnhub's ISO (sprint 14, 3.5)
          ...(exchange                 ? [{ label: "בורסה",        val: exchangeHe(exchange) }] : []),
          ...(industry                 ? [{ label: "ענף",          val: industryHe(industry) }] : []),
          ...(country                  ? [{ label: "מדינה",        val: countryHe(country) }] : []),
          ...(live.shareOutstanding    ? [{ label: "מניות במחזור",  val: `${(live.shareOutstanding / 1000).toFixed(2)}B` }] : []),
        ].map(item => (
          <div key={item.label} style={{ background: UI.surface.page, borderRadius: 6, padding: "10px 12px", border: `1px solid ${UI.line.hairline}` }}>
            <div style={{ ...TYPE.label, fontSize: 12, marginBottom: 4 }}>{item.label}</div>
            <div style={{ ...TYPE.labelLarge, fontWeight: 700 }}>{item.val}</div>
          </div>
        ))}
      </div>

      {/* The senior-management block that used to sit here was a hand-written
          table of names and titles with no source behind it, shipped to
          production as fact. It is gone, and the "מי מנהל" tab of
          CompanyProfile answers the same question from the company's own SEC
          ownership filings. Nothing falls back to the old table. */}
    </div>
  );
}

// ─── Stock News Panel ─────────────────────────────────────────────────────────
// Same pipeline as the news tab — fetchCompanyNews + mapArticle + the shared
// hebrewHeadline chain. This panel used to run its own fetch on the raw Finnhub
// shape, which is why its headlines stayed English: hebrewHeadline reads
// `title`, and the raw payload only carries `headline`. Window and count keep
// the panel's own behaviour: 7 days, 10 items.
// Only articles ABOUT the company (utils/newsMatch.js) · the same rule as the
// news screen and the company page, so a press release that merely names the
// company never lands under its ticker.
function StockNewsPanel({ symbol, name }) {
  const [news, setNews] = useState(null);        // null = loading

  useEffect(() => {
    let alive = true;
    setNews(null);
    fetchCompanyNews(symbol, { days: 7 })
      .then(list => { if (alive) setNews(list.filter(a => isAbout(a, symbol, name && name !== symbol ? name : "") && !isAdvisoryArticle(a)).slice(0, 10)); })
      .catch(() => { if (alive) setNews([]); });
    return () => { alive = false; };
  }, [symbol, name]);

  // The Hebrew from the server (26/10/2026): an article is listed once its
  // Hebrew headline is in, never in English (utils/headline.js).
  const { he: titles, ready, missing } = useHebrewNews(news);

  const loading = news === null || (news.length > 0 && !ready);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32, gap: 10, color: UI.text.muted, fontSize: 13 }}>
        <span className="si-spin" style={{ width: 18, height: 18, border: `2px solid ${UI.line.hairline}`, borderTopColor: UI.interactive, borderRadius: "50%", display: "inline-block" }}/>
        טוען חדשות...
      </div>
    );
  }

  if (!news.length) {
    return (
      <div style={{ textAlign: "center", padding: 32, color: UI.text.muted, fontSize: 13 }}>
        אין כתבות על החברה בשבוע האחרון
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
        <span className="live-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: UI.text.secondary, display: "inline-block" }}/>
        חדשות חיות מ-Finnhub · 7 הימים האחרונים
      </div>
      {news.map((item, i) => {
        const date = new Date(item.datetime * 1000);
        const dateStr = dateNumeric(date);
        const timeStr = date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
        const key = item.id || item.url;
        const he = titles[key];
        // Hebrew only, and the translation is checked too: the English can
        // read as a question the list does not hold while the Hebrew is
        // "כיצד משקיעים …".
        if (!he?.title || isAdvisoryHeadline(he.title)) return null;
        const headline = he.title;
        // The summary only in Hebrew · none, rather than English under Hebrew.
        const summary = he.summary || null;
        return (
          /* Text-only news row — no navigation out of the app. */
          <div key={i}
            style={{ background: UI.surface.page, borderRadius: 6, padding: "12px 14px", border: `1px solid ${UI.line.hairline}` }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Headline text={headline} style={{ fontSize: 16, fontWeight: 700, color: UI.text.primary, lineHeight: 1.4, marginBottom: 5 }}/>
                {summary && summary !== headline && (
                  <div dir={dirOf(summary)} style={{ fontSize: 13, color: UI.text.muted, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{summary}</div>
                )}
              </div>
              {item.image && !isLogoUrl(item.image) && (
                <img src={item.image} alt="" style={{ width: 60, height: 44, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                  onError={e => { e.target.style.display = "none"; }}
                  onLoad={e => { if (looksLikeLogo(e.target.naturalWidth, e.target.naturalHeight)) e.target.style.display = "none"; }}/>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 11, color: UI.text.secondary, fontWeight: 700 }}>{item.source}</span>
              <span style={{ fontSize: 10, color: UI.text.muted }}>{dateStr} · {timeStr}</span>
            </div>
          </div>
        );
      })}
      {waitingNote(missing) && <div style={{ fontSize: 12, color: UI.text.muted }}>{waitingNote(missing)}</div>}
    </div>
  );
}

// ─── דיווחים רשמיים (sprint 21) ─────────────────────────────────────────────
// The company's current reports to the SEC (8-K, 6-K) of the last 14 days,
// newest first, each with what it said (the checked summary) and its links.
// Technical-only reports (exhibits, bylaws) are not listed. A report whose
// summary is not ready, or did not pass its checks, is listed with its link
// and says so. Empty is said as what the stored reports show: the check
// against the SEC runs twice an hour, so a symbol added minutes ago may not
// have been read yet.
const FILINGS_DAYS = 14;
function OfficialFilings({ symbol }) {
  const [list, setList] = useState(undefined);   // undefined = loading · null = failed
  useEffect(() => {
    let alive = true;
    setList(undefined);
    fetchSecFilings([symbol], { days: FILINGS_DAYS, all: true })
      .then(r => { if (alive) setList(r); }, () => { if (alive) setList(null); });
    return () => { alive = false; };
  }, [symbol]);
  return (
    <section data-official-filings style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, color: UI.text.secondary, fontWeight: 600 }}>
        דיווחים רשמיים · דיווחים שוטפים ל-<LTR>SEC</LTR> מ-<LTR className="si-num">{FILINGS_DAYS}</LTR> הימים האחרונים
      </div>
      {list === undefined ? (
        <div style={{ padding: "4px 0" }}><Skeleton h={14} w="40%" mb={8}/><Skeleton h={14} w="90%" mb={6}/><Skeleton h={14} w="75%"/></div>
      ) : list === null ? (
        <div style={{ fontSize: 13, color: UI.text.muted }}>לא ניתן לטעון את הדיווחים כרגע</div>
      ) : !list.length ? (
        <div style={{ fontSize: 13, color: UI.text.muted, lineHeight: 1.6 }}>לא נמצאו דיווחים שוטפים ל-<LTR>SEC</LTR> מ-<LTR className="si-num">{FILINGS_DAYS}</LTR> הימים האחרונים · הבדיקה מול <LTR>SEC EDGAR</LTR> רצה פעמיים בשעה</div>
      ) : list.map(f => (
        <div key={f.accession} style={{ background: UI.surface.page, borderRadius: 6, padding: "12px 14px", border: `1px solid ${UI.line.hairline}` }}>
          <FilingSummary filing={f} compact/>
        </div>
      ))}
      {list?.length > 0 && <div style={{ fontSize: 11.5, color: UI.text.muted }}>סיכום <LTR>AI</LTR> של כל דיווח, מהטקסט שלו בלבד · כל משפט נוקב במסמך שלו</div>}
    </section>
  );
}

// ─── הדוח הבא — the date, and the analyst consensus Finnhub carries for it ──
// The date is a scheduled fact. The estimates are what Finnhub's calendar
// reports as analyst consensus, named as such; when the row has none, only
// the date is shown. No "what to watch", no reading of the numbers.
function NextReport({ symbol }) {
  const [rep, setRep] = useState(null);   // null = unknown or loading → nothing drawn
  useEffect(() => {
    let alive = true;
    setRep(null);
    fetchNextReport(symbol).then(r => { if (alive) setRep(r); }).catch(() => { if (alive) setRep(null); });
    return () => { alive = false; };
  }, [symbol]);
  // Nothing until Finnhub has answered: no skeleton for a line that may not
  // exist, and no "none" for a request that simply did not come back.
  if (rep === null) return null;
  return (
    <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "14px 16px" }}>
      <CardKind kind="report" style={{ marginBottom: 8 }}/>
      {rep.none ? (
        <div style={{ fontSize: 13, color: UI.text.muted }}>אין דוח מתוזמן ב-120 הימים הקרובים בלוח של Finnhub</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: UI.text.secondary }}>הדוח הבא</span>
            <LTR className="si-num" style={{ fontSize: 16, fontWeight: 700, color: UI.text.primary }}>{dateNumeric(rep.date)}</LTR>
            {HOUR_HE[rep.hour] && <span style={{ fontSize: 12.5, color: UI.text.muted }}>· {HOUR_HE[rep.hour]}</span>}
            {rep.quarter && rep.year && <span style={{ fontSize: 12.5, color: UI.text.muted }}>· <LTR>Q{rep.quarter} {rep.year}</LTR></span>}
          </div>
          {/* Analyst estimates follow the same flag as P/E and EPS: the
              company page does not show them either. */}
          {FINNHUB_VALUATION_ENABLED && (rep.epsEstimate != null || rep.revenueEstimate != null) && (
            <div style={{ fontSize: 13, color: UI.text.secondary, marginTop: 6 }}>
              צפי אנליסטים לפי Finnhub:{" "}
              {rep.epsEstimate != null && <>EPS <Amount value={Number(rep.epsEstimate)} currency="USD" decimals={2} size={13} weight={700}/></>}
              {rep.epsEstimate != null && rep.revenueEstimate != null && " · "}
              {rep.revenueEstimate != null && <>הכנסות <CompactMoney value={rep.revenueEstimate} size={13} weight={700}/></>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// A price range, low to high, both ends through the shared Amount (sprint 17).
function PriceRange({ lo, hi }) {
  return (
    <span dir="ltr" style={{ unicodeBidi: "isolate", whiteSpace: "nowrap" }}>
      <Amount value={lo} currency="USD" decimals={2} size={15} weight={700}/> – <Amount value={hi} currency="USD" decimals={2} size={15} weight={700}/>
    </span>
  );
}

// ─── Stock Detail Panel ────────────────────────────────────────────────────────
// `name`, `currentPrice`, `buyPrice` and `lang` are still passed by
// PortfolioTab; the financials tab that consumed them now reads its own
// source, so they are accepted and unused rather than changing the caller.
export default function StockDetailPanel({ symbol, name, currentPrice, prevClose, buyPrice: _buyPrice, cur, onRemove, removeLabel, onClose, onOpenAlerts, shares, pe, divYield, noQuote = false, lang: _lang = "he" }) {
  const [detailTab, setDetailTab] = useState("chart");
  const isMobile = useIsMobile();

  const detailTabs = [
    { id: "chart",     label: "גרף",       icon: "trendUp" },
    { id: "stats",     label: "נתונים",    icon: "chart" },
    // Was "דוחות": a designed empty state waiting for a real source. The
    // source is here now (SEC companyfacts → company_financials), so the tab
    // is the figures themselves. Short label on the phone, where five tabs
    // share one row at 10px.
    { id: "financials",label: isMobile ? "פיננסים" : "נתונים פיננסיים", icon: "clipboard" },
    { id: "profile",   label: "פרופיל",    icon: "building" },
    { id: "news",      label: "חדשות",     icon: "news" },
  ];

  return (
    <div style={{ background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderTop: "none", borderRadius: "0 0 6px 6px", padding: "14px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Close, and the way into the company page: the position, what changed,
          what is coming and the context in the portfolio live there. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <button onClick={() => window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol, name } }))}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "none", minHeight: 44, padding: "0 2px", fontSize: 14, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit" }}>
          <Icon name="building" size={15} color={UI.interactive}/> עמוד החברה
          <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>←</span>
        </button>
        <button onClick={onClose} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "4px 12px", fontSize: 12, color: UI.text.secondary, cursor: "pointer", fontWeight: 600 }}><Icon name="x" size={13} color={UI.text.secondary}/> סגור</button>
      </div>
      {/* מה קרה היום — a holding's move today and what was published about the
          company, before the tabs: the day's fact first, the deep dive after. */}
      {shares != null && (
        <HoldingToday symbol={symbol} name={name} shares={shares} currentPrice={noQuote ? null : currentPrice} prevClose={prevClose} cur={cur}/>
      )}
      <NextReport symbol={symbol}/>
      {/* Sub-tab bar — flex:1 per button on mobile so all five fit without scroll */}
      <div style={{ display: "flex", gap: isMobile ? 3 : 4, paddingBottom: 4 }}>
        {detailTabs.map(t => (
          <button key={t.id} onClick={() => setDetailTab(t.id)} style={{
            background: detailTab === t.id ? UI.state.surface : UI.surface.raised,
            color: detailTab === t.id ? UI.state.fg : UI.text.secondary,
            border: `1px solid ${detailTab === t.id ? UI.state.line : UI.line.hairline}`,
            borderRadius: 6,
            padding: isMobile ? "6px 2px" : "7px 14px",
            // 12px and a 44px target on the phone: at 10px the labels were
            // cut and the buttons were half a finger tall (2026-09-26).
            fontSize: 12, minHeight: isMobile ? 44 : undefined,
            fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            flex: isMobile ? 1 : "none",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
            overflow: "hidden",
          }}>
            {!isMobile && <Icon name={t.icon} size={14} color={detailTab === t.id ? UI.text.primary : UI.text.secondary}/>}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {detailTab === "chart"      && <TradingViewChart symbol={symbol}/>}
      {detailTab === "stats"      && <KeyStatsPanel symbol={symbol}/>}
      {detailTab === "financials" && <FinancialsPanel symbol={symbol}/>}
      {detailTab === "profile"    && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <CompanyProfilePanel symbol={symbol}/>
          <CompanyProfile symbol={symbol}/>
        </div>
      )}
      {detailTab === "news"       && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <OfficialFilings symbol={symbol}/>
          <StockNewsPanel symbol={symbol} name={name}/>
        </div>
      )}

      {/* The marking layer — a holding only, never a watchlist row. */}
      {shares != null && <HoldingMarkCard symbol={symbol}/>}

      {/* Return, weight and the change from cost are on the holding card
          itself since sprint 16 (HoldingCard), so this block repeats none of
          them · only the valuation cells, while that source is on. */}
      {shares != null && FINNHUB_VALUATION_ENABLED && (
        <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted, marginBottom: 10 }}>נתונים מלאים</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 8px" }}>
            {FINNHUB_VALUATION_ENABLED && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: UI.text.muted, marginBottom: 3 }}>P/E</div>
              <div className="si-num" style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary }}>{pe != null ? Number(pe).toFixed(2) : "—"}</div>
            </div>
            )}
            {FINNHUB_VALUATION_ENABLED && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: UI.text.muted, marginBottom: 3 }}>Yield</div>
              <div className="si-num" style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary }}>{divYield != null ? Number(divYield).toFixed(2) + "%" : "—"}</div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* The "מה אם אמכור? / מה אם אקנה עוד?" buttons that opened the
          calculator with this ticker filled in were removed (2026-09-26): the
          user picks the ticker themselves, nothing is suggested. */}
      {onOpenAlerts && (
        <button onClick={() => onOpenAlerts(symbol)}
          style={{ background: UI.accent.soft, border: `1px solid `, borderRadius: 6, padding: "12px 14px", color: UI.accent.base, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%" }}>
          <Icon name="bell" size={16} color={UI.accent.base}/> הגדר התראת מחיר
        </button>
      )}
      <button onClick={onRemove} style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "10px", color: UI.caution, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <Icon name="trash" size={16} color={UI.caution}/> {removeLabel}
      </button>
    </div>
  );
}
